import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import Link from "next/link";
import clsx from "clsx";
import { useDispatch } from "react-redux";

import {
  BRAND_NAME,
  BRAND_PATHS,
  CONSENT_IDS,
  PREFERENCE_IDS,
  PREFERENCE_TYPE_IDS,
  PRODUCT_IDS,
} from "@chef/constants";
import {
  getWeek,
  getYear,
  isConceptPreference,
  isMealboxBasketType,
  isOneSubProduct,
  isPreferencesAllergen,
  isSingelPreference,
  isTastePreference,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";
import {
  AppDispatch,
  resetDeviations,
  useBillingQuery,
  usePreferencesQuery,
  useProductsByIdsQuery,
} from "@chef/state-management";
import { isEqualStrings } from "@chef/utils/equal";
import {
  FlexWrapper,
  PageLoader,
  PortionsMealsSelect,
  PreferenceSelectList,
  PreselectorLoader,
  PreselectedRecipesModal,
  TastePreferencesNotification,
} from "@chef/smart-components";
import {
  getAttribute,
  isAccountAcceptedAllergenStoring,
  isAccountAcceptedDataProcessing,
} from "@chef/state-management/helpers";
import { useEffectOnce, useIntercom } from "@chef/hooks";
import { Close } from "@chef/icons/small";
import { STAGE, usePreselector, useTrack } from "@chef/state-management/hooks";
import { Button, ChatBox, Snackbar } from "@chef/components";
import { getSelectedPreferenceDescription } from "@chef/helpers";
import { isNotEmptyArray } from "@chef/utils/array";

import { ConsentsWarningModal } from "./ConsentsWarningModal";

import { intl } from "./PreselectorQuizPage.Intl";

const schema = yup.object({
  conceptPreferenceIds: yup.array().of(yup.string().required()),
  tastePreferenceIds: yup.array().of(yup.string().required()),
  portions: yup.number().required(),
  meals: yup.number().required(),
});

type PreferencesFormData = yup.InferType<typeof schema>;

export const PreselectorQuizPage = () => {
  useIntercom(false);
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const date = nextDeliveryWeekDateObj();

  const track = useTrack();

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffectOnce(() => {
    track("updateSubscriptionStepViewed", {
      affiliation: "Frontend process",
      change_subscription_details: "Choose mealbox",
      url: window.location.href,
    });
  }, !!track);

  const [openConsentsWarning, setOpenConsentsWarning] = useState(false);

  const { data: billingQuery } = useBillingQuery();
  const { data: preferencesQuery } = usePreferencesQuery();

  const { trigger, stage } = usePreselector({
    onStageChange: {
      [STAGE.COMPLETED]: () => {
        router.push(BRAND_PATHS.CONFIRMATION_OF_PREFERENCE_UPDATE_PAGE);
      },
      [STAGE.COMPLETED_WITH_ALLERGEN]: () => {
        router.push(BRAND_PATHS.CONFIRMATION_OF_PREFERENCE_UPDATE_PAGE);
      },
      [STAGE.FAILED]: () => {
        router.push(BRAND_PATHS.PREFERENCE_ERROR_PAGE);
      },
    },
  });

  const week = getWeek(date);
  const year = getYear(date);

  const { data: productsByIdsQuery } = useProductsByIdsQuery({
    productIds: [PRODUCT_IDS.ONESUB],
    week,
    year,
  });

  const mealboxProduct = productsByIdsQuery?.products.find(isOneSubProduct);

  const hasAcceptedDataProcessing = isAccountAcceptedDataProcessing(
    billingQuery?.billing.consents,
  );

  const hasAcceptedAllergenStoring = isAccountAcceptedAllergenStoring(
    billingQuery?.billing.consents,
  );

  const showConsentDisclaimerFooter =
    !hasAcceptedAllergenStoring || !hasAcceptedDataProcessing;

  const initialMealbox = billingQuery?.billing.baskets
    .find(isMealboxBasketType)
    ?.basketProducts.find((p) => isOneSubProduct(p.variation.product));

  const initialPortions = initialMealbox
    ? getAttribute("Portions", initialMealbox?.variation)
    : undefined;

  const initialMeals = initialMealbox
    ? getAttribute("Meals", initialMealbox?.variation)
    : undefined;

  const methods = useForm<PreferencesFormData>({
    resolver: yupResolver(schema),
    mode: "all",
  });

  const { watch, setValue } = methods;
  const [
    selectedConceptPreferenceIds,
    selectedTastePreferenceIds,
    selectedPortions,
    selectedMeals,
  ] = watch([
    "conceptPreferenceIds",
    "tastePreferenceIds",
    "portions",
    "meals",
  ]);

  useEffect(() => {
    const initialConceptPreferenceIds =
      billingQuery?.billing.preferences
        .filter(isConceptPreference)
        .filter((p) => !isEqualStrings(p.preferenceId, PREFERENCE_IDS.SINGEL))
        .map((p) => p.preferenceId) || [];

    if (
      isNotEmptyArray(initialConceptPreferenceIds) &&
      !selectedConceptPreferenceIds
    ) {
      setValue("conceptPreferenceIds", initialConceptPreferenceIds);
    }
  }, [billingQuery, selectedConceptPreferenceIds, setValue]);

  useEffect(() => {
    const initialTastePreferenceIds =
      billingQuery?.billing.preferences
        .filter(isTastePreference)
        .map((p) => p.preferenceId) || [];

    if (
      isNotEmptyArray(initialTastePreferenceIds) &&
      !selectedTastePreferenceIds
    ) {
      setValue("tastePreferenceIds", initialTastePreferenceIds);
    }
  }, [billingQuery, selectedTastePreferenceIds, setValue]);

  useEffect(() => {
    if (initialPortions && !selectedPortions) {
      setValue("portions", +initialPortions);
    }
  }, [initialPortions, selectedPortions, setValue]);

  useEffect(() => {
    if (initialMeals && !selectedMeals) {
      setValue("meals", +initialMeals);
    }
  }, [initialMeals, selectedMeals, setValue]);

  const isLoading = !productsByIdsQuery || !preferencesQuery || !billingQuery;

  const isSubmitting = stage > 0 && stage < STAGE.COMPLETED;

  if (isLoading) {
    return <PageLoader wholeScreen />;
  }

  const conceptPreferences = preferencesQuery?.preferences
    .filter(
      (p) =>
        isEqualStrings(p.preferenceTypeId, PREFERENCE_TYPE_IDS.CONCEPT) &&
        !isEqualStrings(p.preferenceId, PREFERENCE_IDS.SINGEL),
    )
    .filter((p) => getAttribute("is_fixed", p) === "False");

  const fixedPreferences = preferencesQuery?.preferences
    .filter(
      (p) =>
        isEqualStrings(p.preferenceTypeId, PREFERENCE_TYPE_IDS.CONCEPT) &&
        !isEqualStrings(p.preferenceId, PREFERENCE_IDS.SINGEL),
    )
    .filter((p) => getAttribute("is_fixed", p) === "True");

  const tastePreferences = preferencesQuery?.preferences.filter((p) =>
    isEqualStrings(p.preferenceTypeId, PREFERENCE_TYPE_IDS.TASTE),
  );

  const selectedConceptPreferences = preferencesQuery?.preferences.filter((p) =>
    selectedConceptPreferenceIds?.includes(p.preferenceId),
  );

  const selectedFixedPreferenceIds = selectedConceptPreferences
    ?.filter((p) =>
      fixedPreferences?.some((fp) =>
        isEqualStrings(fp.preferenceId, p.preferenceId),
      ),
    )
    .map((p) => p.preferenceId);

  const disableConceptPreferences = selectedFixedPreferenceIds.length > 0;

  const canSeeTastePreferences = !(
    selectedConceptPreferences?.some(
      (p) => getAttribute("can_see_taste_preferences", p) === "False",
    ) || selectedFixedPreferenceIds.length > 0
  );

  const handleCancel = () => {
    if (hasAcceptedAllergenStoring || hasAcceptedDataProcessing) {
      router.push(BRAND_PATHS.MY_SUBSCRIPTION_LINK);
      return;
    }
    setOpenConsentsWarning(true);
  };

  const onSubmit = async (data: PreferencesFormData) => {
    track("updateSubscriptionStepCompleted", {
      affiliation: "Frontend process",
      changed_subscription_details: "Choose mealbox preferences",
      url: window.location.href,
    });

    let _conceptPreferenceIds = data.conceptPreferenceIds || [];
    let _tastePreferenceIds = data.tastePreferenceIds || [];

    if (data.portions === 1) {
      _conceptPreferenceIds = [PREFERENCE_IDS.SINGEL];
      _tastePreferenceIds = [];
    } else {
      _conceptPreferenceIds = _conceptPreferenceIds.filter(
        (id) => !isSingelPreference({ preferenceId: id }),
      );
    }

    if (isNotEmptyArray(selectedFixedPreferenceIds)) {
      _conceptPreferenceIds = [...selectedFixedPreferenceIds];
      _tastePreferenceIds = [];
    }

    if (!canSeeTastePreferences) {
      _tastePreferenceIds = [];
    }

    const selectedTasteHaveAllergen = isPreferencesAllergen(
      data.tastePreferenceIds || [],
      tastePreferences,
    );

    const requiredConsentIds: string[] = [
      CONSENT_IDS.DATA_PROCESSING[BRAND_NAME],
    ];

    if (selectedTasteHaveAllergen) {
      requiredConsentIds.push(CONSENT_IDS.ALLERGEN_STORING[BRAND_NAME]);
    }

    const variation = mealboxProduct?.variations.find(
      (v) =>
        getAttribute("Portions", v) === data.portions.toString() &&
        getAttribute("Meals", v) === data.meals.toString(),
    );

    if (!variation) {
      return;
    }

    await trigger({
      week,
      year,
      conceptPreferenceIds: _conceptPreferenceIds,
      tastePreferenceIds: _tastePreferenceIds,
      variationId: variation.variationId,
      requiredConsentIds,
    });

    dispatch(resetDeviations());
  };

  return (
    <>
      <ConsentsWarningModal open={openConsentsWarning} />

      <PreselectorLoader stage={stage} />

      <FlexWrapper
        background="white"
        back={{ type: "hidden" }}
        close={{
          type: "button",
          onClick: handleCancel,
          children: <Close />,
        }}
        className="min-h-screen md:min-h-[unset] md:my-16"
      >
        <div className="w-0 h-0" aria-hidden="true" ref={scrollRef} />

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-col h-full"
          >
            <div className="flex flex-col gap-10 p-4 pb-16 text-center">
              <section id="mealbox-selection">
                <h1 className="mb-6 text-xl">
                  <strong>{intl.SELECT_SIZE}</strong>
                </h1>
                <PortionsMealsSelect
                  mealboxProduct={mealboxProduct}
                  animation="default"
                />
              </section>

              <hr className="border-1.5 text-grey-3 my-2" />

              {selectedPortions === 1 && (
                <Snackbar nudge className="text-left">
                  <p className="text-sm">
                    <strong>{intl.PRO_TIP}</strong>{" "}
                    {intl.IF_YOU_WANT_TO_CHOOSE_FULL_RANGE}
                  </p>
                </Snackbar>
              )}
              {selectedPortions !== 1 && (
                <>
                  <section id="concept-preference-selection">
                    <h1 className="text-xl">
                      <strong>{intl.WHAT_DISHES_DO_YOU_LIKE}</strong>
                    </h1>
                    <p className="mt-2 text-sm">
                      {intl.FULL_MENU_IS_ALWAYS_AVAILABLE}
                    </p>
                    <PreferenceSelectList
                      preferences={conceptPreferences}
                      control={methods.control}
                      name="conceptPreferenceIds"
                      className={clsx(
                        disableConceptPreferences && "opacity-30",
                        "flex flex-col gap-3 mt-5 lg:grid lg:grid-cols-2",
                      )}
                      disabled={
                        disableConceptPreferences || (stage > 0 && stage < 6)
                      }
                    />
                    <p className="px-1 pt-4 text-sm">
                      {getSelectedPreferenceDescription({
                        preferenceIds: selectedConceptPreferenceIds || [],
                        preferences: conceptPreferences,
                      })}
                    </p>
                  </section>

                  {fixedPreferences.length > 0 && (
                    <section id="fixed-preference-selection">
                      <strong className="text-base">
                        {intl.OR_SELECT_FIXED}
                      </strong>

                      <PreferenceSelectList
                        preferences={fixedPreferences}
                        name="conceptPreferenceIds"
                        control={methods.control}
                        className="grid grid-cols-1 gap-3 mt-5"
                        disabled={stage > 0 && stage < 6}
                        isFixed
                      />

                      {selectedFixedPreferenceIds.length > 0 && (
                        <ChatBox className="text-sm bg-informationBG">
                          {intl.YOU_HAVE_SELECTED_FIXED}
                        </ChatBox>
                      )}
                    </section>
                  )}

                  <hr className="border-1.5 text-grey-3 my-2" />
                  <section
                    id="taste-preference-selection"
                    className="flex flex-col gap-6"
                  >
                    <h1 className="text-xl">
                      <strong>
                        {intl.INGREDIENTS_YOU}{" "}
                        <span className="px-2 rounded bg-errorBG">
                          {intl.DO_NOT}
                        </span>{" "}
                        {intl.WANT}
                        <div>({intl.OPTIONAL})</div>
                      </strong>
                    </h1>
                    {canSeeTastePreferences && (
                      <>
                        <p className="text-sm">
                          {intl.WE_WILL_DO_OUR_BEST_TO_AVOID_THESE_INGREDIENTS}
                        </p>
                        <PreferenceSelectList
                          preferences={tastePreferences}
                          control={methods.control}
                          name="tastePreferenceIds"
                          className="grid grid-cols-2 gap-3 mt-6 lg:grid-cols-3"
                          disabled={stage > 0 && stage < 6}
                        />
                        <TastePreferencesNotification
                          tastePreferenceIds={selectedTastePreferenceIds || []}
                          conceptPreferenceIds={
                            selectedConceptPreferenceIds || []
                          }
                        />
                      </>
                    )}
                    {!canSeeTastePreferences && (
                      <p className="border-1.5 rounded p-4 border-grey-2">
                        {intl.CANNOT_DESELECT_INGREDIENTS}
                      </p>
                    )}
                  </section>

                  <div className="text-sm text-grey-1">
                    <strong>
                      {intl.IS_THERE_ANYTHING_ELSE_YOU_DO_NOT_WANT}
                    </strong>
                    <p className="lg:text-xs">
                      {intl.MAKE_SURE_TO_READ_RECIPES}
                    </p>
                  </div>
                </>
              )}

              <div className="flex flex-col justify-center mx-auto">
                <Button type="submit" small primary loading={isSubmitting}>
                  {intl.SAVE_CHANGES}
                </Button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="mt-8 underline"
                >
                  <strong> {intl.CANCEL}</strong>
                </button>
              </div>
            </div>
            {showConsentDisclaimerFooter && (
              <div className="flex justify-center p-4 text-center rounded-b bg-background">
                <p className="text-sm">
                  {intl.BY_SAVING_YOU_ACCEPT_TERMS_AND_CONDITIONS}{" "}
                  <Link
                    className="underline"
                    href={BRAND_PATHS.PREFERENCE_INFO_LINK}
                  >
                    {intl.TERMS_AND_CONDITIONS}
                  </Link>
                </p>
              </div>
            )}
          </form>
        </FormProvider>
      </FlexWrapper>

      <PreselectedRecipesModal
        portions={selectedPortions}
        week={week}
        year={year}
      />
    </>
  );
};
